import Stack from '@mui/material/Stack';
import { CSSProperties, FocusEventHandler, Fragment, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { InputBaseStyled, InputsWrap } from './styled';
import { TimeBreakdown, TimeBreakdownStrings } from './types';
import { _castTime, padLeadingZero } from './utils';
import { FormLabel } from '../FormLabel';
import { type InfoIconPopoverProps } from '../InfoIconPopover';
import { SxStyles } from '../types';
import { Typography } from '../Typography';
export * from './hooks';
export * from './utils';

type TimeInput = {
  id: keyof TimeBreakdown;
  placeholder: string;
};

export type DurationPickerV2Props = {
  bgColor?: CSSProperties['backgroundColor'];
  error?: boolean;
  fullWidth?: boolean;
  hours: string;
  id?: string;
  label?: string;
  minutes: string;
  seconds: string;
  disabled?: boolean;
  sx?: SxStyles;
  handleBlurParent?: FocusEventHandler<HTMLFieldSetElement>;
  handleUpdateTime?: (
    timeBreakdownPatch: Partial<TimeBreakdownStrings>
  ) => void;
} & Partial<InfoIconPopoverProps>;

export const DurationPickerV2 = ({
  bgColor = 'transparent',
  error = false,
  fullWidth = false,
  hours,
  id = `durationPicker-${uuidv4()}`,
  label,
  minutes,
  seconds,
  disabled,
  sx,
  handleBlurParent,
  handleUpdateTime,
  infoPopover,
  infoPopoverMaxWidth,
  infoPopoverAnchorOrigin,
  infoPopoverTransformOrigin,
}: DurationPickerV2Props): JSX.Element => {
  const inputsWrapRef = useRef<HTMLDivElement>(null);

  const inputs: TimeInput[] = [
    { id: 'hours', placeholder: 'hh' },
    { id: 'minutes', placeholder: 'mm' },
    { id: 'seconds', placeholder: 'ss' },
  ];

  const breakdown = { hours, minutes, seconds } satisfies TimeBreakdownStrings;

  return (
    <Stack
      component="fieldset"
      role="group"
      aria-labelledby={label ? `${id}-label` : undefined}
      aria-label={!label ? `Duration picker` : undefined}
      sx={{ border: 'none', padding: 0, margin: 0 }}
      onBlur={(e) => {
        const focusIsStillInside = inputsWrapRef.current?.contains(
          e.relatedTarget
        );
        if (focusIsStillInside) {
          e.stopPropagation();
        } else if (handleBlurParent !== undefined) {
          handleBlurParent(e);
        }
      }}
    >
      {(label || infoPopover) && (
        <FormLabel
          id={`${id}-label`}
          htmlFor={id}
          infoPopover={infoPopover}
          infoPopoverMaxWidth={infoPopoverMaxWidth}
          infoPopoverAnchorOrigin={infoPopoverAnchorOrigin}
          infoPopoverTransformOrigin={infoPopoverTransformOrigin}
        >
          {label}
        </FormLabel>
      )}

      <Typography variant="body2" component="span">
        <InputsWrap
          ref={inputsWrapRef}
          bgcolor={bgColor}
          className={error ? 'error' : undefined}
          disabled={disabled}
          maxWidth={fullWidth ? undefined : '10em'}
        >
          {inputs.map(({ id: inputId, placeholder }, i) => {
            const value = breakdown[inputId];
            return (
              <Fragment key={inputId}>
                <InputBaseStyled
                  disabled={disabled}
                  value={value === '' ? value : padLeadingZero(value)}
                  type="number"
                  aria-label={inputId}
                  sx={
                    // Hide default HTML up/down arrows for type="number"
                    {
                      ...sx,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                        {
                          display: 'none',
                        },
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                    }
                  }
                  slotProps={{ input: { min: 0, max: 99 } }}
                  onChange={(e) => {
                    const { value } = e.currentTarget;
                    if (_castTime(value) >= 0) {
                      handleUpdateTime?.({ [inputId]: value });
                    }
                  }}
                  placeholder={placeholder}
                />
                {i < inputs.length - 1 && <span>:</span>}
              </Fragment>
            );
          })}
        </InputsWrap>
      </Typography>
    </Stack>
  );
};
